import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { ApiClientConstant } from '@cureskin/api-client';
import { ProjectedQuestionData } from '@cureskin/api-client/src/server-api/api';
import { ConnectionService } from '@services/connection-service';
import { ConsultationSessionService } from '@services/consultation-session-service';
import { ExperimentService } from '@services/experiment/experiment.service';
import { QuizQuestionEntry } from '@services/experiment/experiment.service.types';
import { LocalStorageService } from '@services/local-storage-service';
import { CommonUtilityHelper } from '@services/common-utility-helper/common-utility-helper';
import { InstantCheckupService } from '@services/instant-checkup-service';
import { QuizGateService } from '@services/quiz-gate-service';
import {
  MULTI_SELECT_SEPARATOR,
  QuizAnswerOutcome,
  QuizOption,
  QuizQuestionLayout,
} from '@services/quiz-chat-service/quiz-chat.types';
import {
  findClientOnlyScreen,
  QUIZ_ANSWERS_STORAGE_KEY,
  QUIZ_SEEN_SCREENS_STORAGE_KEY,
  QUIZ_AUTO_ANSWER_QUESTION_IDS,
  QUIZ_CARRIED_OVER_QUESTION_IDS,
  QUIZ_MANDATORY_HAIR_TOP_PHOTO_ID,
  QuizAnswerMap,
  QuizClientOnlyScreen,
  QuizEngineConfig,
  QuizPhotoPair,
  QuizQuestionInputs,
  QuizPhotoSlot,
  QuizEngineEntry,
} from './consultation-quiz-engine.types';

const INSTANT_CHECKUP_ACTION_NAME = 'InstantCheckup';
const { InputAction, InputType } = ApiClientConstant.Question;
const { MULTI_SELECT } = ApiClientConstant.ChatMessage.Mode;
const { HAIR_FRONT, HAIR_TOP } = ApiClientConstant.InstantCheckup.Type;
const { HAIR } = ApiClientConstant.Regimen.Class;

/** Variant-driven consultation engine. Read this folder's CLAUDE.md before changing the cursor rules. */
@Injectable()
export class ConsultationQuizEngineService {
  private readonly conn = inject(ConnectionService);
  private readonly consultationSessionService = inject(ConsultationSessionService);
  private readonly experimentService = inject(ExperimentService);
  private readonly gateService = inject(QuizGateService);
  private readonly instantCheckupService = inject(InstantCheckupService);
  private readonly commonUtilityHelper = inject(CommonUtilityHelper);
  private readonly localStorageService = inject(LocalStorageService);

  private readonly loadingState = signal<boolean>(true);
  private readonly questionTextState = signal<string>('');
  private readonly questionSubTextState = signal<string>('');
  private readonly layoutState = signal<QuizQuestionLayout | null>(null);
  private readonly imageOptionsState = signal<QuizOption[]>([]);
  private readonly buttonOptionsState = signal<QuizOption[]>([]);
  private readonly textPlaceholderState = signal<string>('');
  private readonly textCtaOptionState = signal<QuizOption | null>(null);
  private readonly photoCtaOptionState = signal<QuizOption | null>(null);
  private readonly photoPairState = signal<QuizPhotoPair | null>(null);
  private readonly questionNumberState = signal<number>(1);
  private readonly isThinkingState = signal<boolean>(false);
  private readonly isCompletedState = signal<boolean>(false);
  private readonly errorMessageState = signal<string>('');
  private readonly requiresFallbackState = signal<boolean>(false);
  private readonly canGoBackState = signal<boolean>(false);
  private readonly currentAnswerState = signal<string | string[] | null>(null);
  private readonly questionOrderState = signal<string[]>([]);

  readonly loading: Signal<boolean> = this.loadingState.asReadonly();
  readonly questionText: Signal<string> = this.questionTextState.asReadonly();
  readonly questionSubText: Signal<string> = this.questionSubTextState.asReadonly();
  readonly layout: Signal<QuizQuestionLayout | null> = this.layoutState.asReadonly();
  readonly imageOptions: Signal<QuizOption[]> = this.imageOptionsState.asReadonly();
  readonly buttonOptions: Signal<QuizOption[]> = this.buttonOptionsState.asReadonly();
  readonly textPlaceholder: Signal<string> = this.textPlaceholderState.asReadonly();
  readonly textCtaOption: Signal<QuizOption | null> = this.textCtaOptionState.asReadonly();
  readonly photoCtaOption: Signal<QuizOption | null> = this.photoCtaOptionState.asReadonly();
  readonly photoPair: Signal<QuizPhotoPair | null> = this.photoPairState.asReadonly();
  readonly questionNumber: Signal<number> = this.questionNumberState.asReadonly();
  readonly isThinking: Signal<boolean> = this.isThinkingState.asReadonly();
  readonly isCompleted: Signal<boolean> = this.isCompletedState.asReadonly();
  readonly errorMessage: Signal<string> = this.errorMessageState.asReadonly();
  readonly requiresFallback: Signal<boolean> = this.requiresFallbackState.asReadonly();
  readonly hasActiveQuestion: Signal<boolean> = computed(() => this.layoutState() !== null);
  /** False on the first screen and once the regimen is allocated — see `submit`. */
  readonly canGoBack: Signal<boolean> = this.canGoBackState.asReadonly();
  /** The stored answer for the screen on display, so the page can pre-fill a revisited question. */
  readonly currentAnswer: Signal<string | string[] | null> = this.currentAnswerState.asReadonly();
  readonly questionOrder: Signal<string[]> = this.questionOrderState.asReadonly();

  private config: QuizEngineConfig | null = null;
  private entries: QuizEngineEntry[] = [];
  private cursor = -1;
  private answerSaveInProcess = false;
  private skippedQuestionIds: string[] = [];

  private furthestIndex = -1;

  configure(config: QuizEngineConfig): void {
    this.config = config;
  }

  /** questionId of the screen on display — the page needs it for photo capture and analytics. */
  currentQuestionId(): string | null {
    return this.entries[this.cursor]?.questionId ?? null;
  }

  async start(): Promise<void> {
    const config = this.requireConfig();
    this.resetRunState();
    try {
      this.gateService.resetCache();
      const configuredEntries = this.buildEntries(config);
      this.questionOrderState.set(configuredEntries.map((entry) => entry.questionId));
      const cached = this.readCachedAnswers();
      this.entries = configuredEntries.map((entry) => ({ ...entry, answer: cached[entry.questionId] ?? null }));
      await this.loadAnswersFromServer();
      await this.moveTo(await this.findNextIndex(-1));
    } catch (error) {
      this.errorMessageState.set(this.toErrorText(error));
    } finally {
      this.loadingState.set(false);
    }
  }

  private buildEntries(config: QuizEngineConfig): QuizQuestionEntry[] {
    const entries = this.orderEntries(config);
    if (config.entries || config.regimenClass !== HAIR) return entries;
    const beforeQuestionId = this.experimentService.getMandatoryHairTopPhotoBeforeQuestionId();
    const anchor = entries.findIndex((entry: QuizQuestionEntry): boolean => entry.questionId === beforeQuestionId);
    if (anchor < 0) return entries;
    return [...entries.slice(0, anchor), { questionId: QUIZ_MANDATORY_HAIR_TOP_PHOTO_ID }, ...entries.slice(anchor)];
  }

  private orderEntries(config: QuizEngineConfig): QuizQuestionEntry[] {
    const configured = config.entries
      ?? this.experimentService.getConsultationQuizQuestionEntries(config.regimenClass);
    const answeredFirst = config.answeredFirstQuestionIds ?? [];
    if (!answeredFirst.length) return configured;
    const answeredFirstEntries = answeredFirst
      .map((questionId) => configured.find((entry) => entry.questionId === questionId) ?? { questionId });
    const remainingEntries = configured.filter((entry) => !answeredFirst.includes(entry.questionId));
    return [...answeredFirstEntries, ...remainingEntries];
  }

  /** The instance can be started more than once (caller-supplied entries, then the variant list). */
  private resetRunState(): void {
    this.entries = [];
    this.cursor = -1;
    this.furthestIndex = -1;
    this.answerSaveInProcess = false;
    this.loadingState.set(true);
    this.isCompletedState.set(false);
    this.requiresFallbackState.set(false);
    this.errorMessageState.set('');
    this.canGoBackState.set(false);
  }

  async submitAnswer(message: string): Promise<QuizAnswerOutcome> {
    const config = this.requireConfig();
    const entry = this.entries[this.cursor];
    if (!entry || this.answerSaveInProcess || this.isCompletedState()) return QuizAnswerOutcome.Ignored;
    if (findClientOnlyScreen(entry.questionId)) {
      if (entry.questionId === QUIZ_MANDATORY_HAIR_TOP_PHOTO_ID) return QuizAnswerOutcome.Ignored;
      this.markScreenSeen(entry.questionId);
      await this.moveTo(await this.findNextIndex(this.cursor));
      return QuizAnswerOutcome.Sent;
    }
    if (!message) return QuizAnswerOutcome.Ignored;
    this.answerSaveInProcess = true;
    this.isThinkingState.set(true);
    this.errorMessageState.set('');
    try {
      const { response } = await this.conn.setQuestionResponse(config.regimenClass as never, entry.questionId, message);
      entry.answer = response;
      this.writeCachedAnswer(entry.questionId, response);
      await this.moveTo(await this.findNextIndex(this.cursor));
      return QuizAnswerOutcome.Sent;
    } catch (error) {
      this.errorMessageState.set(this.toErrorText(error));
      return QuizAnswerOutcome.Ignored;
    } finally {
      this.answerSaveInProcess = false;
      this.isThinkingState.set(false);
    }
  }

  async goBack(): Promise<void> {
    if (!this.canGoBackState()) return;
    const previous = await this.findPreviousIndex(this.cursor);
    if (previous < 0) return;
    await this.moveTo(previous);
  }

  private async loadAnswersFromServer(): Promise<void> {
    const config = this.requireConfig();
    if (!this.entries.length) return;
    const questionIds = this.entries
      .filter((entry) => !findClientOnlyScreen(entry.questionId))
      .map((entry) => entry.questionId);
    const { answers, skipped } = await this.conn.getQuestionResponses(config.regimenClass as never, questionIds);
    this.skippedQuestionIds = skipped ?? [];
    this.entries = this.entries.map((entry) => ({ ...entry, answer: answers?.[entry.questionId] ?? null }));
    const cached = this.readCachedAnswers();
    const refreshed = Object.keys(cached)
      .reduce((result, questionId) => Object.assign(result, { [questionId]: answers?.[questionId] ?? cached[questionId] }), {});
    this.writeCachedAnswers(refreshed);
  }

  private async isRenderable(entry: QuizEngineEntry): Promise<boolean> {
    if (entry.answer !== null) return true;
    return this.gateService.shouldRender(entry);
  }

  private async findNextIndex(from: number): Promise<number> {
    for (let index = from + 1; index < this.entries.length; index += 1) {
      const entry = this.entries[index];
      const clientOnly = findClientOnlyScreen(entry.questionId);
      if (clientOnly) {
        // eslint-disable-next-line no-await-in-loop
        if (await this.shouldShowClientOnlyScreen(clientOnly)) return index;
        // eslint-disable-next-line no-continue
        continue;
      }
      // eslint-disable-next-line no-await-in-loop
      if (entry.answer === null && await this.isRenderable(entry)) return index;
    }
    return -1;
  }

  /** A previous screen has to exist, and the variant has to allow stepping back from where the user is. */
  isAtFurthestQuestion(): boolean {
    return this.cursor >= this.furthestIndex;
  }

  private async canGoBackFrom(index: number): Promise<boolean> {
    if (this.experimentService.isSingleStepBackEnabled() && index < this.furthestIndex) return false;
    return await this.findPreviousIndex(index) >= 0;
  }

  private async findPreviousIndex(from: number): Promise<number> {
    const absorbed = new Set(await this.absorbedByClientOnlyScreenAt(from));
    const answeredHere = this.readCachedAnswers();
    for (let index = from - 1; index >= 0; index -= 1) {
      const entry = this.entries[index];
      if (QUIZ_AUTO_ANSWER_QUESTION_IDS.has(entry.questionId)) continue;
      if (absorbed.has(entry.questionId)) continue;
      if (QUIZ_CARRIED_OVER_QUESTION_IDS.has(entry.questionId) && !answeredHere[entry.questionId]) continue;
      const clientOnly = findClientOnlyScreen(entry.questionId);
      if (clientOnly) {
        // eslint-disable-next-line no-await-in-loop
        if (await this.isClientOnlyBackDestination(clientOnly)) return index;
        continue;
      }
      // eslint-disable-next-line no-await-in-loop
      if (await this.isRenderable(entry)) return index;
    }
    return -1;
  }

  /** Questions the screen at `index` absorbs, and only while every one of them is answered. */
  private async absorbedByClientOnlyScreenAt(index: number): Promise<string[]> {
    const entry = this.entries[index];
    const clientOnly = entry && findClientOnlyScreen(entry.questionId);
    if (!clientOnly || !await this.hasEveryAbsorbedAnswer(clientOnly)) return [];
    return clientOnly.absorbs;
  }

  private async moveTo(index: number): Promise<void> {
    if (index < 0) {
      const { onEntriesExhausted } = this.requireConfig();
      await (onEntriesExhausted ? onEntriesExhausted() : this.submit());
      return;
    }
    if (QUIZ_AUTO_ANSWER_QUESTION_IDS.has(this.entries[index].questionId)) {
      await this.autoAnswer(index);
      return;
    }
    this.cursor = index;
    this.furthestIndex = Math.max(this.furthestIndex, index);
    this.questionNumberState.set(this.entries
      .slice(0, index)
      .filter((entry) => entry.answer !== null && !QUIZ_AUTO_ANSWER_QUESTION_IDS.has(entry.questionId)).length + 1);
    this.canGoBackState.set(await this.canGoBackFrom(index));
    await this.renderCurrentQuestion();
  }

  private async autoAnswer(index: number): Promise<void> {
    const config = this.requireConfig();
    const entry = this.entries[index];
    try {
      const question = await this.conn.getQuestionForUser(config.regimenClass as never, entry.questionId);
      const value = ((question.inputs || []) as unknown as QuizOption[])[0]?.value;
      if (value) {
        const { response } = await this.conn.setQuestionResponse(config.regimenClass as never, entry.questionId, value);
        entry.answer = response;
        this.writeCachedAnswer(entry.questionId, response);
      }
    } catch {
    }
    await this.moveTo(await this.findNextIndex(index));
  }

  private async renderCurrentQuestion(): Promise<void> {
    const config = this.requireConfig();
    const entry = this.entries[this.cursor];
    const clientOnly = findClientOnlyScreen(entry.questionId);
    if (clientOnly) {
      this.renderClientOnlyScreen(clientOnly);
      this.currentAnswerState.set(null);
      return;
    }
    const question = await this.conn.getQuestionForUser(config.regimenClass as never, entry.questionId);
    await this.classifyQuestion(question);
    this.currentAnswerState.set(entry.answer);
  }

  /** Its component owns the content; the engine only sets the layout and clears the previous question's. */
  private async resolvePhotoPair(photoOption: QuizOption | null): Promise<QuizPhotoPair | null> {
    const currentType = this.photoCheckupType(photoOption);
    if (!currentType) return null;
    const alternateType = this.instantCheckupService.getAlternatePhotoType(currentType);
    const user = this.conn.getActingUser();
    const [current, alternate] = await Promise.all([
      this.resolvePhotoSlot(user, currentType),
      this.resolvePhotoSlot(user, alternateType),
    ]);
    return { alternate, current, showPair: !!alternate && this.isAskedFirst(alternateType, currentType) };
  }

  private async resolvePhotoSlot(user: Table.User, checkupType: string): Promise<QuizPhotoSlot | null> {
    if (!user || !checkupType) return null;
    const checkup = await this.instantCheckupService.getRecentInstantCheckupOfType(user, checkupType);
    const url = checkup?.imagePath || '';
    if (!url || await this.commonUtilityHelper.checkImageDeleted(url)) return null;
    return {
      canonicalUrl: url,
      checkupId: checkup?.objectId || '',
      label: this.instantCheckupService.getPhotoTypeLabel(checkupType),
      type: checkupType,
      url,
    };
  }

  private photoCheckupType(photoOption: QuizOption | null): string {
    try {
      return (JSON.parse(photoOption?.params?.queryParams || '{}') as { tag?: string }).tag || '';
    } catch {
      return '';
    }
  }

  private isAskedFirst(alternateType: string, currentType: string): boolean {
    const order = this.questionOrderState();
    const current = order.indexOf(this.instantCheckupService.getPhotoQuestionId(currentType));
    const alternate = order.indexOf(this.instantCheckupService.getPhotoQuestionId(alternateType));
    if (current < 0 || alternate < 0) return true;
    return alternate < current;
  }

  private renderClientOnlyScreen(screen: QuizClientOnlyScreen): void {
    this.layoutState.set(screen.layout);
    this.questionTextState.set('');
    this.questionSubTextState.set('');
    this.imageOptionsState.set([]);
    this.buttonOptionsState.set([]);
    this.textPlaceholderState.set('');
    this.textCtaOptionState.set(null);
    this.photoCtaOptionState.set(null);
    this.photoPairState.set(null);
    if (screen.questionId !== QUIZ_MANDATORY_HAIR_TOP_PHOTO_ID) return;
    this.photoPairState.set({ alternate: null, current: null, showPair: false });
    this.questionTextState.set($localize`:@@quizMandatoryHairTopPhotoTitle:No valid hair photo found`);
    this.questionSubTextState.set($localize`:@@quizMandatoryHairTopPhotoSubText:A valid hair photo is needed to give the most accurate hair treatment. Doctor will check this photo to assess your hair density.`);
    this.photoCtaOptionState.set({
      action: InputAction.ACTION,
      params: { actionName: INSTANT_CHECKUP_ACTION_NAME, queryParams: JSON.stringify({ tag: HAIR_TOP }) },
      text: $localize`:@@quizTakeTopPhoto:Take Top Photo`,
      type: InputType.BUTTON,
    } as QuizOption);
  }

  private async hasEveryAbsorbedAnswer(screen: QuizClientOnlyScreen): Promise<boolean> {
    const absorbed = screen.absorbs
      .map((questionId) => this.entries.find((entry) => entry.questionId === questionId))
      .filter(Boolean);
    if (absorbed.length !== screen.absorbs.length) return false;
    const present = await Promise.all(absorbed.map(async (entry) => entry.answer !== null
      && !(await this.gateService.shouldRender(entry))));
    return present.every(Boolean);
  }

  private async shouldShowClientOnlyScreen(screen: QuizClientOnlyScreen): Promise<boolean> {
    if (screen.questionId === QUIZ_MANDATORY_HAIR_TOP_PHOTO_ID) return !await this.hasValidHairPhoto();
    if (!await this.hasEveryAbsorbedAnswer(screen)) return false;
    return !this.readSeenScreens().includes(screen.questionId);
  }

  private async isClientOnlyBackDestination(screen: QuizClientOnlyScreen): Promise<boolean> {
    if (screen.questionId === QUIZ_MANDATORY_HAIR_TOP_PHOTO_ID) return !await this.hasValidHairPhoto();
    return this.hasEveryAbsorbedAnswer(screen);
  }

  /** A HAIR_FRONT or HAIR_TOP photo in the photo gates' window whose file still exists. Fails open. */
  private async hasValidHairPhoto(): Promise<boolean> {
    try {
      const checkups = await Promise.all([HAIR_FRONT, HAIR_TOP]
        .map((type: string) => this.gateService.findRecentCheckup(type)));
      const deleted = await Promise.all(checkups.map((checkup: { imagePath?: string } | undefined) => !checkup?.imagePath
        || this.commonUtilityHelper.checkImageDeleted(checkup.imagePath)));
      return deleted.includes(false);
    } catch {
      return true;
    }
  }

  private readSeenScreens(): string[] {
    const config = this.requireConfig();
    const store = this.localStorageService.getJsonValue(QUIZ_SEEN_SCREENS_STORAGE_KEY) || {};
    return store[config.consultationSessionId] || [];
  }

  private markScreenSeen(questionId: string): void {
    const config = this.requireConfig();
    const seen = this.readSeenScreens();
    if (seen.includes(questionId)) return;
    const store = this.localStorageService.getJsonValue(QUIZ_SEEN_SCREENS_STORAGE_KEY) || {};
    this.localStorageService.setJsonValue(QUIZ_SEEN_SCREENS_STORAGE_KEY, {
      ...store,
      [config.consultationSessionId]: [...seen, questionId],
    });
  }

  /** Mirrors QuizChatService.classifyQuestion so both engines feed the same render components. */
  private async classifyQuestion(question: ProjectedQuestionData): Promise<void> {
    const parts = this.splitInputs(question);
    const layout = this.detectLayout(question, parts);
    if (!layout) {
      this.requiresFallbackState.set(true);
      return;
    }
    await this.publishQuestion(question, parts, layout);
  }

  private splitInputs(question: ProjectedQuestionData): QuizQuestionInputs {
    const inputs = ((question.inputs || []) as unknown as QuizOption[]).filter((input) => !!input);
    const buttonOptions = inputs.filter((input) => input.type === InputType.BUTTON);
    return {
      buttonOptions,
      imageOptions: inputs.filter((input) => input.type === InputType.IMAGE),
      photoOption: this.findPhotoOption(buttonOptions),
      textInput: inputs.find((input) => input.type === InputType.TEXT) || null,
    };
  }

  /** Null means the question is one this engine cannot render, and the page falls back to the chat one. */
  private detectLayout(question: ProjectedQuestionData, parts: QuizQuestionInputs): QuizQuestionLayout | null {
    const { buttonOptions, imageOptions, photoOption, textInput } = parts;
    if (textInput) return QuizQuestionLayout.Text;
    if (!imageOptions.length && !buttonOptions.length) return null;
    const hasTreeOption = [...imageOptions, ...buttonOptions].some((input) => input.action === InputAction.TREE);
    if (hasTreeOption) {
      return question.mode === MULTI_SELECT ? QuizQuestionLayout.MultiSelect : QuizQuestionLayout.SingleSelect;
    }
    return photoOption ? QuizQuestionLayout.PhotoUpload : null;
  }

  private async publishQuestion(
    question: ProjectedQuestionData, parts: QuizQuestionInputs, layout: QuizQuestionLayout,
  ): Promise<void> {
    const { buttonOptions, imageOptions, photoOption, textInput } = parts;
    const isSelectable = layout === QuizQuestionLayout.SingleSelect || layout === QuizQuestionLayout.MultiSelect;
    const isPhoto = layout === QuizQuestionLayout.PhotoUpload;
    const photoPair = isPhoto ? await this.resolvePhotoPair(photoOption) : null;
    this.photoPairState.set(photoPair);
    this.layoutState.set(layout);
    this.questionTextState.set(question.title || '');
    this.questionSubTextState.set(question.subTitle || '');
    this.imageOptionsState.set(isSelectable ? imageOptions : []);
    this.buttonOptionsState.set(isSelectable ? buttonOptions : []);
    this.textPlaceholderState.set(textInput?.text || '');
    this.textCtaOptionState.set(layout === QuizQuestionLayout.Text
      ? buttonOptions.find((input) => input.action === InputAction.TREE) || null
      : null);
    this.photoCtaOptionState.set(isPhoto ? photoOption : null);
  }

  private findPhotoOption(buttonOptions: QuizOption[]): QuizOption | null {
    return buttonOptions.find((input) => input.action === InputAction.ACTION
      && input.params?.actionName === INSTANT_CHECKUP_ACTION_NAME) || null;
  }

  private async submit(): Promise<void> {
    const config = this.requireConfig();
    this.layoutState.set(null);
    this.isThinkingState.set(true);
    try {
      const { pendingQuestionId, regimenId } = await this.conn.allocateRegimenForConsultationSession(config.consultationSessionId);
      if (regimenId) {
        this.canGoBackState.set(false);
        this.isCompletedState.set(true);
        this.clearQuizStorage();
        return;
      }
      await this.renderPendingQuestion(pendingQuestionId);
    } catch (error) {
      this.errorMessageState.set(this.toErrorText(error));
    } finally {
      this.isThinkingState.set(false);
    }
  }

  private async renderPendingQuestion(pendingQuestionId?: string): Promise<void> {
    if (!pendingQuestionId) {
      this.requiresFallbackState.set(true);
      return;
    }
    const existing = this.entries.findIndex((entry) => entry.questionId === pendingQuestionId);
    if (existing >= 0) {
      this.requiresFallbackState.set(true);
      return;
    }
    this.entries.push({ answer: null, questionId: pendingQuestionId });
    await this.moveTo(this.entries.length - 1);
  }

  private clearQuizStorage(): void {
    const config = this.requireConfig();
    const store = this.localStorageService.getJsonValue(QUIZ_ANSWERS_STORAGE_KEY) || {};
    delete store[config.consultationSessionId];
    this.localStorageService.setJsonValue(QUIZ_ANSWERS_STORAGE_KEY, store);
    const seenStore = this.localStorageService.getJsonValue(QUIZ_SEEN_SCREENS_STORAGE_KEY) || {};
    delete seenStore[config.consultationSessionId];
    this.localStorageService.setJsonValue(QUIZ_SEEN_SCREENS_STORAGE_KEY, seenStore);
    this.consultationSessionService.clearLastBotQuestionId(config.regimenClass);
  }

  private readCachedAnswers(): QuizAnswerMap {
    const config = this.requireConfig();
    const store = this.localStorageService.getJsonValue(QUIZ_ANSWERS_STORAGE_KEY) || {};
    return store[config.consultationSessionId] || {};
  }

  private writeCachedAnswer(questionId: string, answer: string | string[]): void {
    this.writeCachedAnswers({ ...this.readCachedAnswers(), [questionId]: answer });
  }

  /** One map per consultation session, so a second consultation never reads the first one's answers. */
  private writeCachedAnswers(answers: QuizAnswerMap): void {
    const config = this.requireConfig();
    const store = this.localStorageService.getJsonValue(QUIZ_ANSWERS_STORAGE_KEY) || {};
    this.localStorageService.setJsonValue(QUIZ_ANSWERS_STORAGE_KEY, {
      ...store,
      [config.consultationSessionId]: answers,
    });
  }

  private requireConfig(): QuizEngineConfig {
    if (!this.config) throw new Error('ConsultationQuizEngineService used before configure()');
    return this.config;
  }

  private toErrorText(error: unknown): string {
    return (error as { message?: string })?.message || 'Something went wrong. Please try again.';
  }

  /** MULTI_SELECT answers travel joined; the server splits, canonicalises and stores an array. */
  static joinMultiSelect(values: string[]): string {
    return values.join(MULTI_SELECT_SEPARATOR);
  }
}
